import { ceilingToPrecision, roundToPrecision } from '../../helpers/math'
import { DateTime } from 'luxon'
import { BudgetSummary } from './budgetCalculatorModel'

export type OverheadRecoveryMarkup = {
  material: number
  equipment: number
  subcontractor: number
}

export type Season = {
  startDate: string
  endDate: string
  workingDaysPerWeek: number
}

export type ProfitData = {
  overheadRecoveryMarkup: OverheadRecoveryMarkup
  season: Season
  desiredNetProfitMargin: number
  desiredSalesGoal: number
}

export const createDefaultMarkup = (): OverheadRecoveryMarkup => ({
  material: 0,
  equipment: 0,
  subcontractor: 0
})

export const createDefaultSeason = (): Season => {
  const year = new Date().getFullYear()
  return {
    startDate: `${year}-03-01`,
    endDate: `${year}-10-31`,
    workingDaysPerWeek: 5
  }
}

export const createDefaultProfitData = (): ProfitData => ({
  overheadRecoveryMarkup: createDefaultMarkup(),
  season: createDefaultSeason(),
  desiredNetProfitMargin: .15,
  desiredSalesGoal: 0,
})

export const createDefaultDesiredNetProfitMargin = (): number => .15
export const createDefaultDesiredSalesGoal = (): number => 0

export const updateMaterialMarkup = (profit: ProfitData, material: number): ProfitData => {
  return {
    ...profit,
    overheadRecoveryMarkup: {
      ...profit.overheadRecoveryMarkup,
      material: roundToPrecision(material, 4)
    }
  }
}

export const updateEquipmentMarkup = (profit: ProfitData, equipment: number): ProfitData => {
  return {
    ...profit,
    overheadRecoveryMarkup: {
      ...profit.overheadRecoveryMarkup,
      equipment: roundToPrecision(equipment, 4)
    }
  }
}
export const updateSubcontractorMarkup = (profit: ProfitData, subcontractor: number): ProfitData => {
  return {
    ...profit,
    overheadRecoveryMarkup: {
      ...profit.overheadRecoveryMarkup,
      subcontractor: roundToPrecision(subcontractor, 4)
    }
  }
}

export const updateSeasonStartDate = (profit: ProfitData, startDate: string): ProfitData => {
  return {
    ...profit,
    season: {
      ...profit.season,
      startDate
    }
  }
}

export const updateSeasonEndDate = (profit: ProfitData, endDate: string): ProfitData => {
  return {
    ...profit,
    season: {
      ...profit.season,
      endDate
    }
  }
}

export const updateWorkDaysPerWeek = (profit: ProfitData, workingDaysPerWeek: number): ProfitData => {
  return {
    ...profit,
    season: {
      ...profit.season,
      workingDaysPerWeek
    }
  }
}

export const updateDesiredNetProfitMargin = (profit: ProfitData, value: number): ProfitData => {
  return {
    ...profit,
    desiredNetProfitMargin: Math.min(value, .9999)
  }
}

export const updateDesiredSalesGoal = (profit: ProfitData, desiredSalesGoal: number): ProfitData => {
  return {
    ...profit,
    desiredSalesGoal
  }
}

const calculateProductionDays = (season: Season): number => {
  const start = DateTime.fromISO(season.startDate)
  const end = DateTime.fromISO(season.endDate).plus({ days: 1 })
  const duration = end.diff(start)

  return Math.round(duration.as('weeks') * season.workingDaysPerWeek)
}

export type BudgetStatistics = {
  productionDays: number
  totalCOGS: number
  totalOverhead: number
  netProfitMargin: number
  laborMarkup: number
  manHourPrice: number
  salesGoal: number
  grossProfit: number
  grossProfitMargin: number
  netProfit: number
  cogsPercentage: number
  overheadPercentage: number
  breakevenAmount: number
  dailyRevenueToHitSalesGoal: number
  productionDaysToBreakEven: number
  profitMarginFromDesiredSalesGoal: number
  totalManHours: number
  totalBillableHours: number
  directLaborEfficiencyRatio: number
  grossProfitPerManHour: number
  netProfitPerManHour: number
  revenuePerManHour: number
  throughputPerManHour: number
  directLaborRatio: number
  billableHoursPercentage: number
}

export const calculateStatistics = (budgetSummary: BudgetSummary, profit: ProfitData): BudgetStatistics => {
  const { season, overheadRecoveryMarkup, desiredNetProfitMargin, desiredSalesGoal } = profit;
  const totalOverhead = budgetSummary.laborStatistics.unbillableCost + budgetSummary.overhead + budgetSummary.equipmentStatistics.overheadAnnualCost
  const totalCOGS = budgetSummary.labor + budgetSummary.materials + budgetSummary.equipmentStatistics.billableAnnualCost + budgetSummary.subcontractors

  const markupRecovery = budgetSummary.materials * overheadRecoveryMarkup.material +
    budgetSummary.equipmentStatistics.billableAnnualCost * overheadRecoveryMarkup.equipment +
    budgetSummary.subcontractors * overheadRecoveryMarkup.subcontractor
  const laborMarkup = budgetSummary.labor === 0 ? 0 : (totalOverhead - markupRecovery) / budgetSummary.labor

  const laborMarkupPerHour = budgetSummary.laborStatistics.manHourCost * laborMarkup
  const manHourPrice = desiredNetProfitMargin === 1 ? 0 : (budgetSummary.laborStatistics.manHourCost + laborMarkupPerHour) / (1 - desiredNetProfitMargin)

  const salesGoal = roundToPrecision((totalCOGS + totalOverhead) / (1 - desiredNetProfitMargin), 2)
  const grossProfit = salesGoal - totalCOGS
  const grossProfitMargin = salesGoal === 0 ? 0 : grossProfit / salesGoal
  const cogsPercentage = salesGoal === 0 ? 0 : totalCOGS / salesGoal

  const netProfit = salesGoal - totalCOGS - totalOverhead
  const overheadPercentage = salesGoal === 0 ? 0 : totalOverhead / salesGoal
  const breakevenAmount = grossProfitMargin === 0 ? 0 : totalOverhead / grossProfitMargin
  const productionDays = calculateProductionDays(season)
  const dailyRevenueToHitSalesGoal = salesGoal / productionDays
  const productionDaysToBreakEven = dailyRevenueToHitSalesGoal === 0 ? 0 : breakevenAmount / dailyRevenueToHitSalesGoal

  const profitMarginFromDesiredSalesGoal = desiredSalesGoal === 0 ? 0 : (desiredSalesGoal - totalCOGS - totalOverhead) / desiredSalesGoal
  const directLaborEfficiencyRatio = grossProfit / budgetSummary.labor
  const throughput = salesGoal - budgetSummary.materials - budgetSummary.equipmentStatistics.billableAnnualCost - budgetSummary.subcontractors
  return {
    productionDays,
    totalCOGS,
    totalOverhead: roundToPrecision(totalOverhead, 2),
    netProfitMargin: desiredNetProfitMargin,
    laborMarkup: roundToPrecision(laborMarkup, 4),
    manHourPrice: ceilingToPrecision(manHourPrice, 2),
    salesGoal,
    grossProfit,
    grossProfitMargin: roundToPrecision(grossProfitMargin, 4),
    netProfit: roundToPrecision(netProfit, 2),
    cogsPercentage: roundToPrecision(cogsPercentage, 4),
    overheadPercentage: roundToPrecision(overheadPercentage, 4),
    breakevenAmount: roundToPrecision(breakevenAmount, 2),
    dailyRevenueToHitSalesGoal: roundToPrecision(dailyRevenueToHitSalesGoal, 2),
    productionDaysToBreakEven: Math.ceil(productionDaysToBreakEven),
    profitMarginFromDesiredSalesGoal: roundToPrecision(profitMarginFromDesiredSalesGoal, 4),
    directLaborEfficiencyRatio: roundToPrecision(directLaborEfficiencyRatio, 4),
    totalManHours: budgetSummary.laborStatistics.annualHours,
    totalBillableHours: budgetSummary.laborStatistics.billableHours,
    grossProfitPerManHour: roundToPrecision(grossProfit / budgetSummary.laborStatistics.annualHours, 2),
    netProfitPerManHour: roundToPrecision(netProfit / budgetSummary.laborStatistics.annualHours, 2),
    revenuePerManHour: roundToPrecision(salesGoal / budgetSummary.laborStatistics.annualHours, 2),
    throughputPerManHour: roundToPrecision(throughput / budgetSummary.laborStatistics.annualHours, 2),
    directLaborRatio: roundToPrecision(budgetSummary.labor / salesGoal, 4),
    billableHoursPercentage: roundToPrecision(budgetSummary.laborStatistics.billableHours / budgetSummary.laborStatistics.annualHours, 4)
  }
}

export const estimateBreakevenDate = (season: Season, daysToBreakEven: number): string => {
  if(season.workingDaysPerWeek === 0 && daysToBreakEven === 0) {
    return DateTime.fromISO(season.startDate).toISODate()
  }
  const weeks = Math.floor(daysToBreakEven / season.workingDaysPerWeek)
  const days = daysToBreakEven - weeks * season.workingDaysPerWeek

  return DateTime.fromISO(season.startDate).plus({ weeks, days }).toISODate()
}

export const enforceInvariants = (profitData: ProfitData | undefined): ProfitData => {
  if (profitData) {
    return {
      ...profitData,
      season: enforceSeasonInvariants(profitData.season)
    }
  }
  return createDefaultProfitData()
}

const enforceSeasonInvariants = (season: Season): Season => {
  const seasonDefaults = createDefaultSeason()
  return {
    ...season,
    startDate: validDateOrDefault(season.startDate, seasonDefaults.startDate),
    endDate: validDateOrDefault(season.endDate, seasonDefaults.endDate),
    workingDaysPerWeek: valueInRangeOrDefault(season.workingDaysPerWeek, 1, 7, seasonDefaults.workingDaysPerWeek)
  }
}

const valueInRangeOrDefault = (value: number, min: number, max: number, defaultValue: number) => {
  if (value < min || value > max) {
    return defaultValue
  }
  return value
}

const validDateOrDefault = (value: string, defaultValue: string) => {
  if (/^\d{4}[-]\d{2}[-]\d{2}$/.test(value)) {
    return value
  }
  return defaultValue
}
